import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";

import { ApiError } from "../../api/client.ts";
import {
  thumbSize,
  useMediaUrl,
  useDeleteTradeImage,
  useReorderTradeImages,
  useUploadTradeImage,
  type ImageKind,
  type TradeDetail as Trade,
  type TradeImage,
} from "../../api/hooks.ts";
import {
  badgeOf,
  formatBytes,
  formatDay,
  labelImageKind,
  labelOutcome,
} from "../../domain/format.ts";
/* Los dos topes que el navegador puede adelantar viven en `domain/images.ts`:
   los comparte con el material de Social Data, que sube al MISMO almacen. */
import { ACCEPTED_IMAGE_TYPES as ACCEPTED, MAX_FILE_BYTES } from "../../domain/images.ts";
import { usePasteImages } from "../../ui/clipboard.ts";
import { Select } from "../../ui/Field.tsx";
import { Icon } from "../../ui/Icon.tsx";
import { Lightbox, type LightboxShot } from "../../ui/Lightbox.tsx";
import { Modal } from "../../ui/Modal.tsx";
import { useFinePointer } from "../../ui/pointer.ts";
import { takeShots } from "./pendingShots.ts";

/**
 * Las capturas de una operacion: subirlas, verlas grandes y borrarlas
 * (SPEC 14 y 15.6).
 *
 * UNA PETICION POR IMAGEN, EN FILA. El contrato sube de una en una a proposito:
 * con conexion irregular, si falla la tercera no se pierden las dos primeras. Y
 * van en fila —no las cinco a la vez— por tres razones que apuntan al mismo
 * sitio:
 *
 *   1. El servidor pone un `select ... for update` sobre la fila de la operacion
 *      mientras convierte, asi que dos subidas de la MISMA operacion se
 *      serializan de todas formas. Lanzarlas juntas solo mueve la cola de sitio.
 *   2. Con una sola subida en vuelo, el porcentaje que se ve es el de un archivo
 *      concreto. Cinco barras avanzando a trozos en una conexion de 200 kB/s no
 *      informan de nada.
 *   3. La subida en curso se lleva el ancho de banda entero, asi que la primera
 *      captura termina —y queda guardada— en vez de que las cinco vayan al 60 %
 *      y se pierdan todas cuando el ascensor corta la cobertura.
 *
 * El tope de cinco se dice ANTES de que el servidor lo rechace: la zona de
 * subida desaparece al llegar al quinto y el selector no deja elegir de mas.
 * Sigue siendo el servidor el que manda —lo comprueba con el cerrojo puesto y
 * ademas hay un trigger en la base—, pero enterarte al subir la sexta, despues
 * de esperar la conversion, seria enterarte tarde.
 *
 * Un Mentor entra aqui y lo ve todo, sin zona de subida y sin papelera
 * (ADR 0005). ESCONDER NO ES AUTORIZAR: el endpoint pide `TradeWriter` y le
 * responderia 403 igual.
 */

/**
 * El tope de SPEC 14, escrito UNA vez: el modal de alta lo importa de aqui para
 * no dejar pegar mas de lo que esta zona puede subir.
 */
export const MAX_IMAGES = 5;

/**
 * Los `ImageKind` del contrato, en el orden en que se toman las capturas. Los
 * rotulos salen de `labelImageKind`, que es donde viven todas las etiquetas de
 * las enumeraciones (SPEC 15.1): la lista dice CUALES hay, no como se llaman.
 */
const KIND_OPTIONS: readonly { value: ImageKind; label: string }[] = (
  ["before", "during", "after", "higherTimeframe", "lowerTimeframe", "other"] as const
).map((value) => ({ value, label: labelImageKind(value) }));

/**
 * De lo que devuelve el desplegable a un valor del contrato, sin conversiones a
 * la fuerza: lo que no este en la lista es cadena vacia, que el servidor lee
 * como `other`.
 */
function toKind(value: string): ImageKind | "" {
  return KIND_OPTIONS.find((option) => option.value === value)?.value ?? "";
}

type Upload = {
  key: string;
  file: File;
  kind: ImageKind | "";
  state: "waiting" | "sending" | "failed";
  /** Porcentaje ya enviado, de 0 a 100. -1 mientras no se sepa el total. */
  percent: number;
  message: string;
};

export function TradeImages(props: { trade: Trade; canWrite: boolean }): ReactNode {
  const mediaUrl = useMediaUrl();
  const { trade, canWrite } = props;

  const upload = useUploadTradeImage(trade.id);
  const remove = useDeleteTradeImage(trade.id);
  const reorder = useReorderTradeImages(trade.id);

  /* La cola vive en un ref y se copia al estado para pintarla. Al reves —el
     estado como fuente— el bucle de subida leeria la cola que habia cuando
     empezo, y los archivos anadidos a mitad no existirian para el. */
  const queue = useRef<Upload[]>([]);
  const busy = useRef(false);
  const [pending, setPending] = useState<readonly Upload[]>([]);
  const [kind, setKind] = useState<ImageKind | "">("");
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const [shot, setShot] = useState<number | null>(null);
  const [doomed, setDoomed] = useState<TradeImage | null>(null);

  const images = [...trade.images].sort((left, right) => left.sortOrder - right.sortOrder);

  /**
   * MOVER UNA CAPTURA UN SITIO, Y MANDAR LA LISTA ENTERA.
   *
   * No es arrastrar y es a proposito: el propietario registra desde el telefono
   * («talvez arrastrandolas»), y arrastrar con el dedo pide `pointermove`,
   * autoscroll y un punto de soltar, mientras que un toque en una flecha de
   * 44 px hace lo mismo y funciona igual con el teclado y con un lector de
   * pantalla. Con un tope de cinco capturas, poner cualquiera de portada son dos
   * toques como maximo.
   *
   * Lo que se manda es el orden COMPLETO que queda, no «esta sube una»: asi dos
   * toques rapidos seguidos no se pisan.
   */
  function move(index: number, step: -1 | 1): void {
    const next = [...images];
    const other = next[index + step];
    const current = next[index];

    if (other === undefined || current === undefined) return;

    next[index] = other;
    next[index + step] = current;

    reorder.mutate(next.map((image) => image.id));
  }

  /* Para pintar se cuenta `pending`, que es el estado, y no el ref: el ref no
     provoca repintado y la cuenta se quedaria en la de hace un momento. */
  const free = Math.max(0, MAX_IMAGES - images.length - pending.length);
  const sending = pending.find((item) => item.state === "sending");

  function sync(): void {
    setPending([...queue.current]);
  }

  function patch(key: string, change: Partial<Upload>): void {
    queue.current = queue.current.map((item) =>
      item.key === key ? { ...item, ...change } : item,
    );
    sync();
  }

  /**
   * Saca de la cola el siguiente que espera y lo sube. Un solo bucle a la vez:
   * `busy` es lo que impide que dos selecciones seguidas arranquen dos colas.
   */
  async function pump(): Promise<void> {
    if (busy.current) return;

    busy.current = true;

    try {
      for (;;) {
        const next = queue.current.find((item) => item.state === "waiting");

        if (next === undefined) return;

        patch(next.key, { state: "sending", percent: -1, message: "" });

        try {
          await upload({
            file: next.file,
            kind: next.kind,
            onProgress: (progress) => {
              const percent =
                progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : -1;

              /* Solo se repinta cuando el porcentaje cambia de entero: el
                 evento de progreso llega decenas de veces por segundo y
                 repintar en cada uno es trabajo tirado. */
              if (percent !== queue.current.find((item) => item.key === next.key)?.percent) {
                patch(next.key, { percent });
              }
            },
          });

          /* Subida buena: sale de la cola. La captura ya esta en el detalle,
             porque el hook la mezclo en la cache con lo que devolvio el
             servidor: aparece al instante y sin releer nada. */
          queue.current = queue.current.filter((item) => item.key !== next.key);
          sync();
        } catch (failure) {
          /* Fallo: NO sale de la cola. El `File` sigue en memoria y se puede
             reintentar sin volver a abrir el selector, que con el telefono en
             la mano es la diferencia entre reintentar y rendirse. */
          patch(next.key, { state: "failed", message: messageOf(failure) });
        }
      }
    } finally {
      busy.current = false;
    }
  }

  /**
   * Lo que se elige, se arrastra o se PEGA. Aqui se descarta lo que ni siquiera
   * merece viajar —lo que no es imagen, lo que pasa de 8 MB y lo que no cabe en
   * el tope de cinco— y se dice por que, con el nombre del archivo delante.
   */
  function enqueue(files: readonly File[]): void {
    if (files.length === 0) return;

    const excuses: string[] = [];
    const accepted: Upload[] = [];
    let room = MAX_IMAGES - images.length - queue.current.length;

    for (const file of files) {
      if (!ACCEPTED.includes(file.type)) {
        excuses.push(`${file.name}: no es un PNG, un JPEG ni un WebP.`);
        continue;
      }

      if (file.size > MAX_FILE_BYTES) {
        excuses.push(
          `${file.name}: ocupa ${formatBytes(file.size)} y el tope por captura es ${formatBytes(MAX_FILE_BYTES)}.`,
        );
        continue;
      }

      if (room <= 0) {
        excuses.push(`${file.name}: una operacion guarda ${MAX_IMAGES} capturas como mucho.`);
        continue;
      }

      room -= 1;
      accepted.push({
        key: `${Date.now()}-${file.name}-${accepted.length}`,
        file,
        kind,
        state: "waiting",
        percent: -1,
        message: "",
      });
    }

    setRejected(excuses);

    if (accepted.length === 0) return;

    queue.current = [...queue.current, ...accepted];
    sync();
    void pump();
  }

  function retry(key: string): void {
    patch(key, { state: "waiting", message: "", percent: -1 });
    void pump();
  }

  function forget(key: string): void {
    queue.current = queue.current.filter((item) => item.key !== key);
    sync();
  }

  /* Ctrl+V. En un ordenador la captura recien tomada ya esta en el
     portapapeles, asi que guardarla en un archivo y volver a buscarla son dos
     pasos para nada. Se escucha en la VENTANA y no en la zona de arrastre:
     nadie va a enfocar una zona de arrastre antes de pegar. Un pegado de texto
     no se toca, porque `usePasteImages` solo actua si vienen imagenes. */
  usePasteImages(canWrite && free > 0, (files) => enqueue(files));

  const fine = useFinePointer();

  /* `enqueue` se rehace en cada repintado; el ref es lo que deja que el efecto
     de montaje lo llame sin depender de el. */
  const enqueueNow = useRef(enqueue);

  useEffect(() => {
    enqueueNow.current = enqueue;
  });

  /* Lo pegado en el modal de alta se recoge aqui, una sola vez y solo si es de
     ESTA operacion: alli no habia `id` al que subir y aqui si, con el progreso
     por archivo y el reintento de siempre. */
  useEffect(() => {
    if (!canWrite) return;

    const waiting = takeShots(trade.id);

    if (waiting.length > 0) enqueueNow.current(waiting.map((shot) => shot.file));
  }, [canWrite, trade.id]);

  const shots: LightboxShot[] = images.map((image) => ({
    contentHash: image.contentHash,
    width: image.width,
    height: image.height,
    title: image.caption ?? image.originalFileName,
    meta: `${trade.instrumentCode} · ${labelImageKind(image.kind)} · ${formatDay(trade.tradeDateLocal)}`,
    badgeText: trade.outcome === null ? "Abierta" : labelOutcome(trade.outcome),
    note: null,
    badgeTone: badgeOf(trade.outcome),
    /* Ya estas en la operacion: el enlace llevaria a la pagina que tienes
       detras del visor. */
    tradeId: null,
  }));

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-h">
        <div>
          <h3>Capturas</h3>
          <p>
            {images.length === 0
              ? `Hasta ${MAX_IMAGES} por operacion`
              : `${images.length} de ${MAX_IMAGES} · el grafico es la mitad del relato`}
          </p>
        </div>
        <Icon name="image" />
      </div>

      <div className="panel-b">
        {images.length === 0 && !canWrite && (
          <p className="hint">Esta operacion no tiene capturas.</p>
        )}

        {(images.length > 0 || pending.length > 0) && (
          <div className="previews">
            {images.map((image, index) => (
              <figure
                /* `ord` solo cuando las flechas se pintan: es lo que le deja al
                   nombre el hueco de la derecha libre, y sin ellas ese hueco
                   seria un recorte sin motivo. */
                className={canWrite && images.length > 1 ? "pv ord" : "pv"}
                key={image.id}
              >
                <button
                  type="button"
                  className="pv-open"
                  title={`${labelImageKind(image.kind)} · ${image.originalFileName}`}
                  aria-label={`Ver en grande: ${image.caption ?? image.originalFileName}`}
                  onClick={() => setShot(index)}
                >
                  <img
                    src={mediaUrl(image.contentHash, "thumb")}
                    width={thumbSize(image).width}
                    height={thumbSize(image).height}
                    loading="lazy"
                    alt=""
                  />
                </button>
                <figcaption className="pv-name">
                  {index === 0 && <Icon name="star" />}
                  {index === 0 ? "Portada" : labelImageKind(image.kind)}
                </figcaption>
                {canWrite && images.length > 1 && (
                  /* Solo con dos o mas: con una captura las flechas no llevan a
                     ningun sitio y ocuparian el hueco del nombre. */
                  <div className="pv-ord">
                    <button
                      type="button"
                      title="Moverla hacia el principio"
                      aria-label={`Mover ${image.originalFileName} hacia el principio`}
                      disabled={index === 0 || reorder.isPending}
                      onClick={() => move(index, -1)}
                    >
                      <Icon name="prev" />
                    </button>
                    <button
                      type="button"
                      title="Moverla hacia el final"
                      aria-label={`Mover ${image.originalFileName} hacia el final`}
                      disabled={index === images.length - 1 || reorder.isPending}
                      onClick={() => move(index, 1)}
                    >
                      <Icon name="next" />
                    </button>
                  </div>
                )}
                {canWrite && (
                  <button
                    type="button"
                    className="pv-del"
                    title="Borrar esta captura"
                    aria-label={`Borrar la captura ${image.originalFileName}`}
                    onClick={() => setDoomed(image)}
                  >
                    <Icon name="x" />
                  </button>
                )}
              </figure>
            ))}

            {pending.map((item) => (
              <div className={item.state === "failed" ? "pv bad" : "pv up"} key={item.key}>
                {item.state === "failed" ? (
                  /* Solo la marca. Reintentar y descartar viven en la lista de
                     abajo, donde caben dos botones de 44 px y la frase entera
                     del servidor; dentro de una miniatura de 104 px no caben ni
                     una cosa ni la otra. */
                  <div className="pv-why">
                    <Icon name="x" />
                    <span>No subio</span>
                  </div>
                ) : (
                  <>
                    <span className="pv-pct">
                      {item.state === "waiting"
                        ? "En cola"
                        : item.percent < 0
                          ? "Subiendo"
                          : `${item.percent} %`}
                    </span>
                    <div className="pv-track">
                      <div
                        className="pv-fill"
                        style={{ width: `${item.percent < 0 ? 0 : item.percent}%` }}
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {canWrite && images.length > 1 && (
          <p className="hint">
            <b>La primera es la portada:</b> es la que sale en el timeline y en las ramas. Las
            flechas de cada miniatura la mueven de sitio y se guarda al instante.
          </p>
        )}

        {/* Si el servidor rechaza el orden, se dice: la galeria ya volvio a como
            estaba y sin el motivo pareceria que el boton no hizo nada. Pasa
            cuando se subio o se borro una captura desde otro sitio. */}
        {reorder.error !== null && <p className="errbox">{messageOf(reorder.error)}</p>}

        {/* Un solo anuncio para el lector de pantalla, y no uno por miniatura:
            cinco regiones vivas hablando a la vez no informan de nada. */}
        <p className="hint" aria-live="polite">
          {sending === undefined
            ? ""
            : `Subiendo ${sending.file.name}${sending.percent < 0 ? "" : `, ${sending.percent} %`}. Quedan ${pending.length - 1} en cola.`}
        </p>

        {canWrite && free > 0 && (
          <>
            {/* Que las capturas se suben SOLAS hay que decirlo, porque no se
                nota: el propietario creyo que era "Guardar la revision" el que
                las subia, y ese boton no toca los archivos. */}
            <p className="infobox" style={{ marginTop: 14 }}>
              <b>Cada captura se guarda sola en cuanto la sueltas.</b> No hay que darle a ningun
              boton: empieza a subir al instante y la barra de arriba dice por donde va. El boton
              de la revision, mas abajo, es solo para el texto que escribas.
            </p>

            <div style={{ marginTop: 14, maxWidth: 320 }}>
              <Select
                label="Que retratan las que subas ahora"
                value={kind}
                onChange={(value) => setKind(toKind(value))}
                options={KIND_OPTIONS}
                placeholder="Sin clasificar"
                hint="Se guarda con cada captura y se puede cambiar eligiendo otro antes de subir la siguiente."
              />
            </div>

            <div
              className={over ? "dropzone over" : "dropzone"}
              onDragOver={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                setOver(false);
                enqueue([...event.dataTransfer.files]);
              }}
            >
              <Icon name="upload" />
              <b>
                {fine
                  ? "Arrastra tus capturas, pega una con Ctrl+V o haz clic para elegirlas"
                  : "Arrastra tus capturas o toca para elegirlas"}
              </b>
              <small>
                PNG, JPEG o WebP, hasta {formatBytes(MAX_FILE_BYTES)} cada una. Quedan {free} de{" "}
                {MAX_IMAGES}. Se suben de una en una, asi que si una falla las otras siguen.
              </small>
              {/* Ctrl+V solo se anuncia donde existe: se pregunta por el
                  puntero fino, no por el ancho de la ventana ni por el agente
                  de usuario. En un telefono no hay Ctrl+V que ofrecer. */}
              {fine && (
                <small>
                  Con la captura en el portapapeles basta Ctrl+V en cualquier parte de esta
                  pantalla, sin tener que pulsar aqui antes.
                </small>
              )}
              <input
                type="file"
                multiple
                accept={ACCEPTED.join(",")}
                aria-label="Elegir capturas para subir"
                onChange={(event) => {
                  enqueue(event.target.files === null ? [] : [...event.target.files]);
                  /* El mismo archivo dos veces seguidas no dispara `change` si
                     el valor no se limpia, y reintentar la misma captura es
                     justo lo que uno hace cuando la primera fallo. */
                  event.target.value = "";
                }}
              />
            </div>
          </>
        )}

        {/* El tope, dicho ANTES de que el servidor lo rechace. Se mira
            `images`, no `free`: con cinco en la cola tambien queda sitio cero,
            pero ahi lo que pasa es que estan subiendo, no que sobren. */}
        {canWrite && images.length >= MAX_IMAGES && (
          <p className="infobox" style={{ marginTop: 14 }}>
            <b>Esta operacion ya tiene sus {MAX_IMAGES} capturas.</b> Borra una para subir otra.
          </p>
        )}

        {rejected.length > 0 && (
          <ul className="up-errors">
            {rejected.map((excuse) => (
              <li key={excuse}>{excuse}</li>
            ))}
          </ul>
        )}

        {pending.some((item) => item.state === "failed") && (
          <ul className="up-errors">
            {pending
              .filter((item) => item.state === "failed")
              .map((item) => (
                <li key={item.key}>
                  <b>{item.file.name}</b> — {item.message}
                  <span className="row">
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => retry(item.key)}
                    >
                      <Icon name="refresh" />
                      Reintentar
                    </button>
                    <button
                      type="button"
                      className="btn ghost sm"
                      onClick={() => forget(item.key)}
                    >
                      <Icon name="x" />
                      Descartar
                    </button>
                  </span>
                </li>
              ))}
          </ul>
        )}
      </div>

      {shot !== null && shots.length > 0 && (
        <Lightbox
          shots={shots}
          index={shot}
          onIndex={setShot}
          onClose={() => setShot(null)}
        />
      )}

      {doomed !== null && (
        <Modal
          narrow
          title="Borrar la captura"
          onClose={() => setDoomed(null)}
          footer={
            <>
              <button type="button" className="btn ghost" onClick={() => setDoomed(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(doomed.id, { onSuccess: () => setDoomed(null) })
                }
              >
                {remove.isPending ? <span className="spinner" /> : <Icon name="trash" />}
                {remove.isPending ? "Borrando…" : "Borrar la captura"}
              </button>
            </>
          }
        >
          {remove.error !== null && <p className="errbox">{messageOf(remove.error)}</p>}

          <p className="infobox">
            <b>Se quita de esta operacion.</b> El archivo solo desaparece del disco si ninguna
            otra entrada usa esa misma captura; si la subiste dos veces, la otra sigue
            funcionando.
          </p>

          <p className="hint">
            {doomed.originalFileName} · {labelImageKind(doomed.kind)} · {doomed.width}×
            {doomed.height} · {formatBytes(doomed.byteSize)}
          </p>
        </Modal>
      )}
    </section>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.fieldError("file") ?? error.message;

  return "No se pudo subir. Revisa la conexion y reintenta.";
}
